"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader as Loader2, User, Mail, Phone, Calendar, MapPin, Car, MessageSquare, CircleCheck as CheckCircle2 } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 bg-black/80 backdrop-blur-xl border border-primary/30 rounded-lg text-center shadow-strong animate-reveal">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-lg bg-primary/10 mb-6 border border-primary/20">
                    <CheckCircle2 className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-3xl font-heading font-bold text-white mb-4 uppercase tracking-tight" style={{
        fontFamily: 'var(--font-heading)'
      }}>Request Received</h3>
                <p className="text-lg text-gray-300 leading-relaxed">{successMessage}</p>
            </div>;
  }
  const inputClasses = "rounded-sm border-primary/30 bg-white/90 h-12 focus-visible:ring-primary focus-visible:border-primary transition-all duration-80 text-black placeholder:text-gray-400 focus:scale-[1.01] active:scale-[0.99]";
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-white font-semibold flex items-center gap-2">
                                <User className="size-4 text-primary" /> Full Name
                            </FormLabel>
                            <FormControl>
                                <Input placeholder="John Doe" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email_address" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-white font-semibold flex items-center gap-2">
                                <Mail className="size-4 text-primary" /> Email Address
                            </FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="phoenixenerprises@gmail.com" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone_number" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-white font-semibold flex items-center gap-2">
                                <Phone className="size-4 text-primary" /> Phone Number
                            </FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="323-492-6292" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="trip_datetime" rules={{
        required: "Preferred Date & Time is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-white font-semibold flex items-center gap-2">
                                <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate> Date & Time
                            </FormLabel>
                            <FormControl>
                                <Input type="text" placeholder="MM/DD/YYYY HH:MM" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="pickup_location" rules={{
        required: "Pickup Location is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-white font-semibold flex items-center gap-2">
                                <MapPin className="size-4 text-primary" /> Pickup Location
                            </FormLabel>
                            <FormControl>
                                <Input type="text" placeholder="e.g., LAX Terminal 4, Los Angeles, CA" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="vehicle_type" rules={{
        required: "Vehicle Type is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-white font-semibold flex items-center gap-2">
                                <Car className="size-4 text-primary" /> Vehicle Type
                            </FormLabel>
                            <FormControl>
                                <select className={`flex w-full min-w-0 px-3 py-1 text-base outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none ${inputClasses}`} {...field}>
                                    <option value="" disabled>Select a vehicle</option>
                                    <option value="Luxury Sedan">Luxury Sedan</option>
                                    <option value="SUV">SUV</option>
                                    <option value="Stretch Limo">Stretch Limo</option>
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="special_requests" render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-white font-semibold flex items-center gap-2">
                                <MessageSquare className="size-4 text-primary" /> Special Requests
                            </FormLabel>
                            <FormControl>
                                <textarea placeholder="Any additional details (e.g., child seat, wheelchair access)..." className={`flex w-full min-w-0 px-3 py-3 text-base outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y min-h-[100px] ${inputClasses.replace('h-12', '')}`} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-sm">
                    <p className="text-red-200 text-sm">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg rounded-sm bg-[#C5A059] hover:bg-[#B8935A] transition-all duration-80 text-black font-bold uppercase tracking-widest shadow-medium mt-4 hover:scale-[1.02] active:scale-[0.98]">
                {isSubmitting ? <>
                        <Loader2 className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : 'Request Quote'}
            </Button>
            <p className="text-center text-[10px] text-gray-400 uppercase tracking-widest mt-4">
                Secure Booking • Professional Service • 24/7 Support
            </p>
        </Form>;
}
export default function ReservationRequest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="reservation-request" className="relative py-28 min-h-[900px] flex items-center overflow-hidden bg-black">
            <div className="absolute inset-0 z-0">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/luxury_suv_la_night.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/luxury_suv_la_night.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/luxury_suv_la_night.png-wvc-sizes" alt="Luxury black SUV in Los Angeles at night" className="w-full h-full object-cover object-center opacity-60" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                    <div ref={ref} className={`w-full lg:w-1/2 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                        <div className="max-w-xl">
                            <div className="inline-block px-4 py-1 border border-primary/40 rounded-lg bg-primary/5 mb-6">
                                <span className="text-primary text-xs font-bold uppercase tracking-[0.2em]">Elite Reservations</span>
                            </div>
                            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tighter leading-[1.1]" style={{
              fontFamily: 'var(--font-heading)'
            }}>
                                Secure Your <span className="text-primary">Journey</span>
                            </h2>
                            <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8">
                                Experience the pinnacle of professional mobility. Fill out the details below to request a personalized quote for your luxury transportation needs in Los Angeles.
                            </p>
                            
                            <div className="grid grid-cols-2 gap-8 border-t border-white/10 pt-8">
                                <div>
                                    <p className="text-primary font-bold text-2xl mb-1">24/7</p>
                                    <p className="text-gray-400 text-sm uppercase tracking-wider">Availability</p>
                                </div>
                                <div>
                                    <p className="text-primary font-bold text-2xl mb-1">VIP</p>
                                    <p className="text-gray-400 text-sm uppercase tracking-wider">Standards</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`w-full lg:w-1/2 transition-all duration-1000 delay-200 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <div className="backdrop-blur-md rounded-lg p-8 md:p-10 lg:p-12 shadow-strong border border-primary/20 relative overflow-hidden" style={{
            backgroundColor: 'var(--backdrop-blur)'
          }}>
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>
                            
                            <div className="mb-8">
                                <h3 className="font-heading text-2xl font-bold text-white mb-2 uppercase tracking-tight" style={{
                fontFamily: 'var(--font-heading)'
              }}>
                                    Booking Request
                                </h3>
                                <div className="h-1 w-12 bg-primary"></div>
                            </div>

                            <FormProvider formId="booking_form_master" sectionName="Reservation Request" defaultValues={{
              full_name: "",
              email_address: "",
              phone_number: "",
              trip_datetime: "",
              pickup_location: "",
              vehicle_type: "",
              special_requests: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email_address: {
                required: true,
                pattern: "email"
              },
              phone_number: {
                required: true
              },
              trip_datetime: {
                required: true
              },
              pickup_location: {
                required: true
              },
              vehicle_type: {
                required: true
              },
              special_requests: {}
            }} submitText="Request Quote" successMessage="Thank you! Your booking request has been received. Phoenix Enterprises Limousine Service will contact you shortly to confirm your reservation.">
                                <ReservationForm />
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </div>
        </section>;
}